/**
 * Sport sub-type scheduling helpers — the browser twin of
 * backend/utils/sportPlans.js (generateSessionDates / computeTeacherPay), so
 * the schedule modal can preview dates and totals live. The server recomputes
 * everything on submit; keep the two in step.
 */

export const MAX_PLAN_SESSIONS = 60;
// Coaches per session / group, the first one included (sportPlans.MAX_COACHES).
export const MAX_COACHES = 10;
export const TEACHER_PAY_UNITS = ['session', 'hour'];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const round2 = (n) => Math.round(n * 100) / 100;

export const dateKeyToDate = (key) => new Date(`${key}T00:00:00.000Z`);
export const dateToKey = (d) => d.toISOString().slice(0, 10);

/** Today's date as YYYY-MM-DD in the viewer's local calendar. */
export function localTodayKey(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function timeToMinutes(hhmm) {
  if (!TIME_RE.test(hhmm || '')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function addMinutes(hhmm, minutes) {
  const s = timeToMinutes(hhmm);
  if (s === null || !Number.isFinite(Number(minutes))) return '';
  const total = s + Number(minutes);
  if (total > 23 * 60 + 59) return '';
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function durationBetween(start, end) {
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  if (s === null || e === null || e <= s) return null;
  return e - s;
}

/** '' / null / NaN / negative -> null; otherwise rounded to cents. */
export function toMoneyOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return round2(n);
}

export function computeTeacherPay(rate, unit, durationMinutes) {
  const r = toMoneyOrNull(rate);
  if (r === null) return null;
  if ((unit || 'session') === 'session') return r;
  const d = Number(durationMinutes);
  if (!Number.isFinite(d) || d <= 0) return 0;
  return round2(r * (d / 60));
}

/** Same contract as the backend: { dates } or { error }. */
export function generateSessionDates({ startDate, mode = 'single', weekdays, count }) {
  if (!DATE_RE.test(startDate || '')) return { error: 'startDate' };
  const start = dateKeyToDate(startDate);
  if (Number.isNaN(start.getTime()) || dateToKey(start) !== startDate) return { error: 'startDate' };
  if (mode === 'single') return { dates: [startDate] };
  const n = parseInt(count, 10);
  if (!Number.isFinite(n) || n < 1 || n > MAX_PLAN_SESSIONS) return { error: 'count' };
  let days = Array.isArray(weekdays) ? weekdays.map(Number).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6) : [];
  if (!days.length) days = [start.getUTCDay()];
  const set = new Set(days);
  const dates = [];
  const cursor = new Date(start);
  for (let i = 0; dates.length < n && i < n * 7 + 7; i++) {
    if (set.has(cursor.getUTCDay())) dates.push(dateToKey(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return { dates };
}

/**
 * Totals for the preview card. `clientCount` is how many clients the plan
 * bills (group members, or 1 for a single client).
 */
export function summarizePlan({ dates, sessionPrice, teacherPayRate, teacherPayUnit, durationMinutes, clientCount }) {
  const n = Array.isArray(dates) ? dates.length : 0;
  const price = toMoneyOrNull(sessionPrice) || 0;
  const payPerSession = computeTeacherPay(teacherPayRate, teacherPayUnit, durationMinutes);
  const perClient = round2(price * n);
  return {
    sessionCount: n,
    perClient,
    totalCharges: round2(perClient * (price > 0 ? clientCount || 0 : 0)),
    teacherPayPerSession: payPerSession,
    totalTeacherPay: round2((payPerSession || 0) * n),
  };
}

/** A weekday's short name in the UI language, 0 = Sunday. */
export function weekdayLabel(day, locale) {
  // 2026-10-04 is a Sunday; UTC so the label never shifts with the timezone.
  const d = new Date(Date.UTC(2026, 9, 4 + day));
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(d);
  } catch {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day];
  }
}

// ---------------------------------------------------------------------------
// Weeks (Sunday–Saturday, Israel). Mirrored in backend/utils/sportWeek.js.
// ---------------------------------------------------------------------------

export function addDaysKey(key, n) {
  const d = dateKeyToDate(key);
  d.setUTCDate(d.getUTCDate() + n);
  return dateToKey(d);
}

/** Today's date (YYYY-MM-DD) in Israel, whatever the device timezone. */
export function israelTodayKey(now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  } catch {
    return localTodayKey(now);
  }
}

/** The Sunday of the week a date key falls in. */
export function weekStartKey(key) {
  return addDaysKey(key, -dateKeyToDate(key).getUTCDay());
}

export const currentWeekKey = (now = new Date()) => weekStartKey(israelTodayKey(now));
export const nextWeekKey = (now = new Date()) => addDaysKey(currentWeekKey(now), 7);

/** "05/10 – 11/10" */
export function weekRangeText(weekKey) {
  const f = (k) => `${k.slice(8, 10)}/${k.slice(5, 7)}`;
  return `${f(weekKey)} – ${f(addDaysKey(weekKey, 6))}`;
}

const GROUP_COLORS = [
  { bg: '#e0f2fe', fg: '#075985', bar: '#0284c7' },
  { bg: '#dcfce7', fg: '#166534', bar: '#16a34a' },
  { bg: '#fef3c7', fg: '#92400e', bar: '#d97706' },
  { bg: '#ede9fe', fg: '#5b21b6', bar: '#7c3aed' },
  { bg: '#fce7f3', fg: '#9d174d', bar: '#db2777' },
  { bg: '#ccfbf1', fg: '#115e59', bar: '#0d9488' },
  { bg: '#fee2e2', fg: '#991b1b', bar: '#dc2626' },
  { bg: '#e2e8f0', fg: '#1e293b', bar: '#475569' },
];

/** A stable colour per group (the same group is the same colour everywhere). */
export function groupColor(groupId) {
  const s = String(groupId || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return GROUP_COLORS[h % GROUP_COLORS.length];
}

export function formatMoney(amount, locale) {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'ILS', maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `₪${amount}`;
  }
}
